import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useHistory } from 'react-router-dom';
import {
  FaSearch,
  FaFilter,
  FaList,
  FaThumbsUp,
  FaThumbsDown,
  FaTimes,
  FaEnvelope,
  FaPrint,
  FaCloud,
  FaFolder,
  FaCamera,
} from 'react-icons/fa';
import { toast } from 'react-toastify';

import { useAuth } from '~/hooks';
import api from '~/services/api';
import { Spinner } from '~/components/Spinner';
import SimpleCameraModal from '~/components/SimpleCameraModal';

import {
  Container,
  Header,
  FilterContainer,
  ProtocolList,
  ProtocolItem,
  SearchContainer,
  FilterButton,
  CameraButton,
  EmptyMessage,
  Pagination,
  StatusBadge,
} from './styles';

const MobileProtocolList = () => {
  const { company } = useAuth();
  const history = useHistory();

  const [protocolsLoading, setProtocolsLoading] = useState(false);
  const [protocols, setProtocols] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  // cameraMode: 'barcode' para scan, 'photo' para foto (pode ser expandido no futuro)
  const [cameraMode, setCameraMode] = useState('barcode');

  // Usar os mesmos filtros da versão desktop mas simplificados
  const [startPeriod, setStartPeriod] = useState(() => {
    const date = new Date();
    date.setDate(1);
    return date;
  });

  const [endPeriod, setEndPeriod] = useState(() => {
    const date = new Date();
    return date;
  });

  // Carregar protocolos
  const loadProtocols = useCallback(async () => {
    if (!company?.id) return;

    try {
      setProtocolsLoading(true);

      const response = await api.get('/protocols', {
        params: {
          company_id: company.id,
          cod: searchTerm,
          situation: [3, 4],
          page: currentPage,
          start_period: startPeriod,
          end_period: endPeriod,
        },
      });

      const { docs, pages } = response.data;

      setProtocols(docs || []);
      setTotalPages(pages || 1);
    } catch (error) {
      toast.error(`Erro ao carregar protocolos. ${error}`);
    } finally {
      setProtocolsLoading(false);
    }
  }, [company?.id, currentPage, searchTerm, startPeriod, endPeriod]);

  useEffect(() => {
    loadProtocols();
  }, [loadProtocols]);

  // Função para navegar para a página de detalhes
  const handleProtocolClick = useCallback(
    protocolId => {
      history.push(`/mobile-protocol/${protocolId}`);
    },
    [history]
  );

  // Lidar com mudança de página
  const handlePageChange = useCallback(page => {
    setCurrentPage(page);
  }, []);

  // Lidar com busca
  const handleSearch = useCallback(
    e => {
      e.preventDefault();
      loadProtocols();
    },
    [loadProtocols]
  );

  // Função chamada quando código de barras é detectado
  const handleBarcodeDetected = useCallback(
    async code => {
      setSearchTerm(code);
      toast.info(`Código detectado: ${code}. Buscando protocolo...`);
      try {
        const response = await api.get('/protocols', {
          params: {
            company_id: company.id,
            cod: code,
            situation: [3, 4],
            page: 1,
          },
        });
        const { docs } = response.data;
        if (docs && docs.length > 0) {
          const protocol = docs[0];
          // Redireciona para o Detail já com o modal aberto e data do dia
          history.push({
            pathname: `/mobile-protocol/${protocol.id}`,
            state: {
              openWriteOff: true,
              writeOffDate: new Date().toISOString().slice(0, 10),
            },
          });
          toast.success(`Protocolo ${protocol.cod} encontrado!`);
        } else {
          toast.warning(`Nenhum protocolo encontrado para o código: ${code}`);
        }
      } catch (error) {
        toast.error(`Erro ao buscar protocolo: ${error}`);
      }
    },
    [company?.id, history]
  );

  return (
    <Container>
      <Header>
        <h2>Protocolos</h2>
        <div className="header-buttons">
          {/* Botão para abrir o leitor de código de barras */}
          <CameraButton
            onClick={() => {
              setCameraMode('barcode');
              setShowCamera(true);
            }}
          >
            <FaCamera />
          </CameraButton>
          {/* Se quiser adicionar um botão para tirar foto direto da lista, descomente abaixo */}
          {/*
          <CameraButton
            onClick={() => {
              setCameraMode('photo');
              setShowCamera(true);
            }}
            title="Tirar Foto"
          >
            <FaCamera />
            <span style={{ marginLeft: 8 }}>Tirar Foto</span>
          </CameraButton>
          */}
          <FilterButton
            onClick={() => setShowFilters(!showFilters)}
            title="Filtros"
          >
            <FaFilter />
          </FilterButton>
        </div>
      </Header>

      {showFilters && (
        <FilterContainer>
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="startDate">De:</label>
              <input
                id="startDate"
                type="date"
                value={format(startPeriod, 'yyyy-MM-dd')}
                onChange={e => setStartPeriod(new Date(e.target.value))}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="endDate">Até:</label>
              <input
                id="endDate"
                type="date"
                value={format(endPeriod, 'yyyy-MM-dd')}
                onChange={e => setEndPeriod(new Date(e.target.value))}
              />
            </div>
          </div>

          <button type="button" onClick={loadProtocols}>
            Aplicar Filtros
          </button>
        </FilterContainer>
      )}

      <SearchContainer onSubmit={handleSearch}>
        <input
          type="text"
          placeholder="Buscar por codigo do protocolo..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        <button type="submit">
          <FaSearch />
        </button>
      </SearchContainer>

      {protocolsLoading ? (
        <div className="loading-container">
          <Spinner size={30} />
          <p>Carregando protocolos...</p>
        </div>
      ) : protocols.length === 0 ? (
        <EmptyMessage>
          <FaList size={40} />
          <p>Nenhum protocolo encontrado</p>
          <button type="button" onClick={loadProtocols}>
            Tentar novamente
          </button>
        </EmptyMessage>
      ) : (
        <>
          <ProtocolList>
            {protocols.map(protocolItem => (
              <ProtocolItem
                key={protocolItem.id}
                onClick={() => handleProtocolClick(protocolItem.id)}
              >
                <div className="protocol-header">
                  <span className="protocol-code">#{protocolItem.cod}</span>
                  <span className="protocol-date">
                    {format(new Date(protocolItem.created_at), 'dd/MM/yyyy', {
                      locale: ptBR,
                    })}
                  </span>
                </div>
                <div className="protocol-body">
                  <p className="client-name">
                    {protocolItem.client?.nickname ||
                      protocolItem.client?.name ||
                      'Cliente não informado'}
                  </p>
                </div>
                <div className="protocol-footer">
                  <StatusBadge
                    className={
                      protocolItem.situation === 2
                        ? 'concluido'
                        : protocolItem.situation === 5
                        ? 'cancelado'
                        : 'pendente'
                    }
                  >
                    {protocolItem.situation !== 2 &&
                      protocolItem.situation !== 5 && (
                        <>
                          <FaThumbsDown size={18} color="#E53935" />
                          Pendente
                        </>
                      )}
                    {protocolItem.situation === 2 && (
                      <>
                        <FaThumbsUp size={18} color="#006229" />
                        Entregue
                      </>
                    )}
                    {protocolItem.situation === 5 && (
                      <>
                        <FaTimes size={18} color="#E53935" />
                        Cancelado
                      </>
                    )}
                  </StatusBadge>
                  <span className="protocol-method">
                    {protocolItem.method === 0 && (
                      <div className="method-icon">
                        <FaEnvelope size={16} color="#01579B" />
                        <span>Email</span>
                      </div>
                    )}
                    {protocolItem.method === 1 && (
                      <div className="method-icon">
                        <FaPrint size={16} color="#006229" />
                        <span>Impresso</span>
                      </div>
                    )}
                    {protocolItem.method === 2 && (
                      <div className="method-icon">
                        <FaCloud size={16} color="#44546a" />
                        <span>Digital</span>
                      </div>
                    )}
                    {protocolItem.method === 3 && (
                      <div className="method-icon">
                        <FaFolder size={16} color="#006229" />
                        <span>Pasta</span>
                      </div>
                    )}
                  </span>
                </div>
              </ProtocolItem>
            ))}
          </ProtocolList>

          <Pagination>
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              Anterior
            </button>
            <span>
              Página {currentPage} de {totalPages}
            </span>
            <button
              type="button"
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              Próxima
            </button>
          </Pagination>
        </>
      )}

      {/* Modal da câmera: só mostra o botão de scan se cameraMode for 'barcode' */}
      <SimpleCameraModal
        isOpen={!!showCamera}
        setIsOpen={setShowCamera}
        onBarcodeDetected={
          cameraMode === 'barcode' ? handleBarcodeDetected : null
        }
        onPhotoTaken={null}
      />
    </Container>
  );
};

export default MobileProtocolList;
